import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Container, Carousel, Alert, ReviewCard, Button, List } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs, Image, LoadingOverlay, CTA } from '../../components';
import { useReviews } from '../../hooks/use-reviews';

import RatingsFeature from '../../components/page/ratings/feature';
import CardSection from '../../components/common/card-section';
import ProductIntro from '../../components/page/ratings/product-intro';
import ProductLineRatings from '../../components/page/ratings/product-line-ratings';
import CustomerReviews from '../../components/common/customer-reviews';
import LocalNavTop from '../../components/common/local-nav-top';
import ElfSightEmbed from '../../components/common/elfsight-embed';
import LocalBusiness from '../../components/common/structured-data/local-business';

import './index.css';

const Ratings = (props) => {
	const pageData = props.data.contentstackRatings;
	const configData = props.data.contentstackGlobalConfig?.reviews;
	const { status, reviews } = useReviews('branch', pageData.branch_data?.[0]?.ratings_id ? null : pageData.branch);
	const [showAllReviews, setShowAllReviews] = useState(false);

	useEffect(() => {
		if (reviews?.Reviews?.length > 0 && reviews?.Reviews?.length <= 12) setShowAllReviews(true);
	}, [reviews?.Empty, reviews?.Reviews?.length]);

	const onMoreReviewsClick = () => setShowAllReviews(true);

	return (
		<Layout id='template-Ratings'>
			{pageData?.branch_data?.[0]?.local_nav?.[0] && (
				<LocalNavTop
					branchData={pageData.branch_data[0]}
					linksArray={pageData.branch_data[0].local_nav}
					isAnchors={false}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			{status === 'loading' && <LoadingOverlay />}

			{pageData.page_components?.[0] &&
				pageData.page_components.map((block, index) => {
					if (block.hero?.page_hero?.[0]) {
						return (
							<div
								className='ratingHero relative'
								key={index}
							>
								{block.hero.page_hero[0].image?.[0] && (
									<Image
										data={block.hero.page_hero[0].image[0]}
										aspectRatio='3/1'
										className='block max-h-[30vw] w-full'
									/>
								)}

								<div className='ratingHero-content md:py4 bg-black py-3'>
									<Container>
										{block.hero.page_hero[0].heading && (
											<h1
												className='font-semibold text-white'
												dangerouslySetInnerHTML={{ __html: block.hero.page_hero[0].heading }}
											/>
										)}
										{block.hero.page_hero[0].description && (
											<h1
												className='font-semibold text-white'
												dangerouslySetInnerHTML={{ __html: block.hero.page_hero[0].description }}
											/>
										)}
										{block.hero.page_hero[0].cta?.[0] && (
											<List bullets='none'>
												{block.hero.page_hero[0].cta.map((cta, index) => (
													<li key={index}>
														<CTA data={cta} />
													</li>
												))}
											</List>
										)}
									</Container>
								</div>
							</div>
						);
					}

					if (block.headings != null && block.headings.heading_style === 'feature') {
						return (
							<RatingsFeature
								data={block.headings}
								key={index}
							/>
						);
					}

					if (block.headings != null && block.headings.heading_style !== 'feature') {
						return (
							<div
								key={index}
								className={`pt-6 md:pt-8 xl:pt-12 ${block.headings.heading_style === 'center' ? 'text-center' : ''}`}
							>
								<Container>
									{block.headings.heading && (
										<div
											className='text-lg font-semibold md:text-xl xl:text-32px'
											dangerouslySetInnerHTML={{ __html: block.headings.heading }}
										/>
									)}
								</Container>
							</div>
						);
					}

					if (block.cards?.cards?.[0]) {
						return (
							<CardSection
								data={block.cards}
								key={index}
							/>
						);
					}

					if (block.product_feature != null) {
						return (
							<ProductIntro
								data={block.product_feature}
								className='py-6 md:py-8 xl:py-12'
								rating={reviews?.OverallSatisfactionRating}
								totalReviews={reviews?.TotalReviews}
								key={index}
							/>
						);
					}

					if (block.photo_panel != null && block.photo_panel.image?.[0]) {
						return (
							<div
								key={index}
								className='ratings-photoPanel py-6 md:py-8 xl:py-12'
							>
								<Container>
									{block.photo_panel.heading && <h2 className='mb-4 font-semibold xl:mb-6'>{block.photo_panel.heading}</h2>}

									<Carousel
										className='ratings-photoCarousel'
										slidePadding={10}
										showArrows
									>
										{block.photo_panel.image.map((image, index) => (
											<div key={index}>
												<Image data={image} />
											</div>
										))}
									</Carousel>
								</Container>
							</div>
						);
					}

					if (block.ratings_by_pl != null) {
						return (
							<ProductLineRatings
								data={block.ratings_by_pl}
								ratingCode={block.ratings_by_pl.ratings_code?.[0]?.ratings_code}
								className={`py-6 md:py-8 xl:py-12 ${block.ratings_by_pl.gray_background ? 'bg-gray-5' : ''}`}
								key={index}
							/>
						);
					}

					if (status !== 'loading' && block.reviews != null && (reviews?.Empty || reviews?.Reviews?.[0]) && pageData.ratings_type === 'showroom') {
						return (
							<div
								key={index}
								className='relative py-6 md:py-8 xl:py-12'
								id={block.reviews.anchor_text}
							>
								<Container>
									{block.reviews.heading && <h2 className='mb-4 font-semibold xl:mb-6'>{block.reviews.heading}</h2>}
									{reviews?.Empty && (
										<Alert
											type='info'
											hideClose
										>
											{reviews.Empty}
										</Alert>
									)}

									{reviews?.Reviews?.[0] && (
										<div className={!showAllReviews ? 'h-96 overflow-hidden md:h-screen' : ''}>
											<div className={`columns-1 md:columns-2 md:gap-6 xl:columns-3`}>
												{reviews.Reviews.map((review, index) => {
													const date = new Date(review.DateSurveyed);

													return (
														<div
															className='mb-4 break-inside-avoid md:mb-6'
															key={index}
														>
															<ReviewCard
																rating={review.OverallSatisfactionRating}
																date={date?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
															>
																{review.WhyChosePella && (
																	<>
																		<ReviewCard.Heading content={configData?.heading_1} />
																		<ReviewCard.Content content={review.WhyChosePella} />
																	</>
																)}
																{review.MemorableAspects && (
																	<>
																		<ReviewCard.Heading content={configData?.heading_2} />
																		<ReviewCard.Content content={review.MemorableAspects} />
																	</>
																)}

																<ReviewCard.Author name={review.CustomerFirstName + ' ' + review.CustomerLastName} />
															</ReviewCard>
														</div>
													);
												})}
											</div>
										</div>
									)}
								</Container>

								{reviews?.Reviews?.length > 12 && !showAllReviews && (
									<div className='absolute bottom-6 left-0 z-2 w-full bg-gradient-to-t from-white px-5 py-20 text-center md:bottom-8 xl:bottom-12'>
										<Button
											color='primary'
											onClick={onMoreReviewsClick}
										>
											{block.reviews.cta}
										</Button>
									</div>
								)}
							</div>
						);
					}

					if (pageData.branch_data?.[0]?.ratings_id && block.reviews != null && pageData.ratings_type === 'product line') {
						return (
							<div
								key={index}
								className='block-reviews-elfsight relative py-6 md:py-8 xl:py-12'
								id={block.reviews.anchor_text}
							>
								<Container>
									<ElfSightEmbed elfsightId={pageData.branch_data[0].ratings_id} />
								</Container>
							</div>
						);
					}

					if (!pageData.branch_data?.[0]?.ratings_id && block.reviews != null && (reviews?.Empty || reviews?.Reviews?.[0]) && pageData.ratings_type === 'product line') {
						return (
							<div
								key={index}
								className='relative py-6 md:py-8 xl:py-12'
								id={block.reviews.anchor_text}
							>
								<CustomerReviews
									title={block.reviews.heading}
									data={reviews}
									totalReviews={reviews.TotalReviews}
									overallRating={reviews.OverallSatisfactionRating}
								/>
							</div>
						);
					}
				})}

			<LocalBusiness
				name={pageData.seo.title}
				url={pageData.url}
			/>
		</Layout>
	);
};

Ratings.propTypes = {
	data: PropTypes.object,
};

export default Ratings;

// eslint-disable-next-line react/prop-types -- gatsby component
export const Head = ({ data }) => <SEO {...data.contentstackRatings.seo} />;

export const query = graphql`
	query RatingsQuery($uid: String!) {
		contentstackRatings(id: { eq: $uid }) {
			url
			breadcrumbs {
				active_page
				back_pages {
					...CTA
				}
			}
			ratings_type
			ratings_tag {
				ratings_code
			}
			branch_data {
				...BranchData
			}
			branch
			page_components {
				hero {
					page_hero {
						...NewHero
					}
				}
				headings {
					heading_style
					heading
					subheading
					image {
						...Image
						image {
							url
							gatsbyImageData(quality: 90)
						}
					}
				}
				cards {
					...CardSection
				}
				product_feature {
					image {
						...Image
						image {
							url
							gatsbyImageData(quality: 90)
						}
					}
					heading
					material
					description
					cta {
						...CTA
					}
				}
				photo_panel {
					heading
					image {
						...Image
						image {
							url
							gatsbyImageData(quality: 90)
						}
					}
				}
				ratings_by_pl {
					gray_background
					image {
						...Image
						image {
							url
							gatsbyImageData(quality: 90)
						}
					}
					heading
					material
					ratings_code {
						ratings_code
					}
					cta {
						...CTA
					}
				}
				reviews {
					anchor_text
					heading
					cta: cta_
				}
			}
			seo {
				...SEO
			}
		}

		contentstackGlobalConfig {
			reviews {
				heading
				description
				heading_1
				heading_2
			}
		}
	}
`;
